import { useEffect } from 'react';

import { useShallow } from 'zustand/react/shallow';

import SocketService from '@src/services/SocketService';
import { useCalculatorStore, useNotificationStore, usePeersStore } from '@src/stores';

import {
  ECalculatorSocketEvents,
  ICalculatorOperationSyncPayload,
  IShareCalculatorResponse,
  ISharedCalculatorState,
  IStopSharingCalculatorResponse,
} from '@src/types';

const useCalculatorSocketListener = ({ isConnected = false }: { isConnected: boolean }) => {
  const { setSharedCalculatorData, resetSharedCalculatorData, setSharedCalculatorState } = useCalculatorStore(
    useShallow((state) => ({
      setSharedCalculatorData: state.setSharedCalculatorData,
      resetSharedCalculatorData: state.resetSharedCalculatorData,
      setSharedCalculatorState: state.setSharedCalculatorState,
      sharedCalculatorData: state.sharedCalculatorData,
      setIsPersonalCalculatorVisible: state.setIsPersonalCalculatorVisible,
    }))
  );

  const { appendNotification } = useNotificationStore(
    useShallow((state) => ({
      appendNotification: state.appendNotification,
    }))
  );

  const { getPeerInfoById } = usePeersStore(
    useShallow((state) => ({
      getPeerInfoById: state.getPeerInfoById,
    }))
  );

  useEffect(() => {
    if (!isConnected) return;

    const handleShareCalculator = (data: IShareCalculatorResponse) => {
      const { calculatorDetails } = data;

      if (!calculatorDetails) {
        resetSharedCalculatorData();
        return;
      }

      const { owner, settings, currentOperation } = calculatorDetails;

      const wasSharedByMe = useCalculatorStore.getState().sharedCalculatorData.isSharedByMe;

      setSharedCalculatorData({
        isOpen: true,
        isSharedByMe: wasSharedByMe,
        owner,
        settings,
      });

      const expression = currentOperation?.expression ?? '';
      const result = currentOperation?.result ?? '';
      setSharedCalculatorState({ expression, result });
    };

    const handleStopShareCalculator = (data?: IStopSharingCalculatorResponse) => {
      const forceStoppedByHost = data?.forceStoppedByHost ?? false;
      const wasSharedByMe = useCalculatorStore.getState().sharedCalculatorData.isSharedByMe;

      resetSharedCalculatorData();

      if (wasSharedByMe || forceStoppedByHost) {
        const stoppedByPeerId = data?.from_peer_id;
        const peerInfo = stoppedByPeerId ? getPeerInfoById(stoppedByPeerId) : null;
        const peerName = peerInfo?.peer_name ?? data?.peer_name;

        appendNotification({
          text: `${peerName} has stopped your calculator sharing`,
          source: 'calculator',
        });
      }
    };

    SocketService.on(ECalculatorSocketEvents.SHARE_CALCULATOR, handleShareCalculator);
    SocketService.on(ECalculatorSocketEvents.STOP_SHARING_CALCULATOR, handleStopShareCalculator);

    return () => {
      SocketService.off(ECalculatorSocketEvents.SHARE_CALCULATOR, handleShareCalculator);
      SocketService.off(ECalculatorSocketEvents.STOP_SHARING_CALCULATOR, handleStopShareCalculator);
    };
  }, [
    isConnected,
    resetSharedCalculatorData,
    setSharedCalculatorData,
    setSharedCalculatorState,
    appendNotification,
    getPeerInfoById,
  ]);

  useEffect(() => {
    if (!isConnected) return;

    const handleUpdateCalculator = (data: IShareCalculatorResponse) => {
      const { calculatorDetails } = data;
      if (!calculatorDetails) return;

      const { owner, settings, currentOperation } = calculatorDetails;

      setSharedCalculatorData({
        isOpen: true,
        isSharedByMe: false,
        owner,
        settings,
      });

      if (currentOperation) {
        setSharedCalculatorState({
          expression: currentOperation.expression ?? '',
          result: currentOperation.result ?? '',
        });
      }
    };

    SocketService.on(ECalculatorSocketEvents.UPDATE_CALCULATOR, handleUpdateCalculator);

    return () => {
      SocketService.off(ECalculatorSocketEvents.UPDATE_CALCULATOR, handleUpdateCalculator);
    };
  }, [isConnected, resetSharedCalculatorData, setSharedCalculatorData, setSharedCalculatorState]);

  useEffect(() => {
    if (!isConnected) return;

    const handleCalculatorOperationsSync = (data: ICalculatorOperationSyncPayload) => {
      const expression = data?.calculatorOperation?.expression ?? '';
      const result = data?.calculatorOperation?.result ?? '';

      const currentSharedState: ISharedCalculatorState = useCalculatorStore.getState().sharedCalculatorState;
      if (currentSharedState.expression === expression && currentSharedState.result === result) return;

      setSharedCalculatorState({ expression, result });
    };

    SocketService.on(ECalculatorSocketEvents.CALCULATOR_OPERATIONS_SYNC, handleCalculatorOperationsSync);

    return () => {
      SocketService.off(ECalculatorSocketEvents.CALCULATOR_OPERATIONS_SYNC, handleCalculatorOperationsSync);
    };
  }, [isConnected, setSharedCalculatorState]);
};

export default useCalculatorSocketListener;
